import { useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  CaretLeft,
  CaretRight,
  DownloadSimple,
  Sparkle,
  UploadSimple,
  X,
} from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { shrinkImage } from "@/lib/shrinkImage";
import { money } from "@/lib/format";

const GROUPS = [
  ["kitchen", "Kitchen Spaces"],
  ["bathroom", "Bathroom Spaces"],
];

// "See it in your home + instant estimate" on a public slab page. Everything here is
// gated by the AI images settings (visualiser / photo upload / instant quote).
export const SlabVisualiser = ({
  slabKey,
  stone,
  onQuote,
  enabled = true,
  onConfig,
}) => {
  const [cfg, setCfg] = useState(null);
  const [group, setGroup] = useState("kitchen");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState("");
  const [shot, setShot] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const stripRef = useRef(null);
  const onConfigRef = useRef(onConfig);
  onConfigRef.current = onConfig;

  useEffect(() => {
    const settle = (c) => {
      setCfg(c);
      onConfigRef.current?.(c);
    };
    fetch(`${API}/public/ai-images/config`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(settle)
      .catch(() =>
        settle({ visualiser: false, instant_quote: false, sample_rooms: [] }),
      );
  }, []);

  useEffect(() => {
    if (!file) {
      setPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const samples = useMemo(
    () => (cfg?.sample_rooms || []).filter((r) => r.group === group),
    [cfg, group],
  );

  const showVis = !!cfg?.visualiser && enabled;
  if (!cfg || (!showVis && !cfg.instant_quote)) return null;

  const take = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setError("Please choose a JPG, PNG or WEBP photo.");
      return;
    }
    setError("");
    setShot(null);
    setFile(f);
  };

  const run = async (sampleKey = "") => {
    setBusy(sampleKey || "upload");
    setError("");
    try {
      const fd = new FormData();
      fd.append("key", slabKey);
      if (sampleKey) fd.append("sample", sampleKey);
      else if (file) fd.append("file", await shrinkImage(file));
      const res = await fetch(`${API}/public/ai-images/visualise-photo`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "That did not work");
      setShot(data);
    } catch (err) {
      setError(err.message || "That did not work — please try again.");
    }
    setBusy("");
  };

  const scroll = (dir) =>
    stripRef.current?.scrollBy({ left: dir * 280, behavior: "smooth" });

  return (
    <section
      id="slab-visualiser"
      className="mt-16 scroll-mt-28 sm:mt-20"
      data-testid="slab-visualiser"
    >
      <div>
        <div
          className={`grid gap-6 ${showVis && cfg.instant_quote ? "grid-cols-[minmax(0,1fr)] 2xl:grid-cols-[1.2fr_0.8fr]" : "grid-cols-[minmax(0,1fr)]"}`}
        >
          {showVis && (
            <div className="s-card s-still min-w-0 !p-6 sm:!p-8">
              <div className="flex items-start gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center  bg-[#2e2c29] text-white">
                  <Camera size={22} weight="bold" />
                </span>
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                    Get your AI visualization + instant quote
                  </h2>
                  <p className="mt-1 text-sm text-[color:var(--s-mute)]">
                    Upload a photo of your space to see {stone || "this stone"}{" "}
                    on your counters, with a ballpark price right beside it.
                  </p>
                </div>
              </div>

              {shot && !busy ? (
                <div className="mt-6" data-testid="visualiser-result">
                  <div className="s-card-fig overflow-hidden">
                    <img
                      src={`${API}/ai-images/image/${shot.path}`}
                      alt={`${stone} in ${shot.room}`}
                      data-testid="visualiser-image"
                      className="w-full object-cover"
                    />
                  </div>
                  <p className="mt-3 text-xs text-[color:var(--s-mute)]">
                    {shot.disclaimer}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button
                      type="button"
                      data-testid="visualiser-quote"
                      onClick={onQuote}
                      className="s-btn"
                    >
                      Get a free quote on this stone
                    </button>
                    <a
                      href={`${API}/ai-images/image/${shot.path}`}
                      download
                      data-testid="visualiser-download"
                      className="s-ghost"
                    >
                      <DownloadSimple size={14} weight="bold" /> Save the
                      picture
                    </a>
                    <button
                      type="button"
                      data-testid="visualiser-again"
                      onClick={() => {
                        setShot(null);
                        setFile(null);
                      }}
                      className="s-ghost"
                    >
                      Try another photo
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {cfg.photo_upload && (
                    <div
                      data-testid="visualiser-dropzone"
                      onDragEnter={(e) => {
                        e.preventDefault();
                        setDrag(true);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDrag(true);
                      }}
                      onDragLeave={() => setDrag(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDrag(false);
                        take(e.dataTransfer.files?.[0]);
                      }}
                      className={`mt-6  border-2 border-dashed p-6 text-center transition-colors duration-200 sm:p-8 ${
                        drag
                          ? "border-[#2e2c29] bg-[#f3f1ec]"
                          : "border-[#d8d3ca] bg-[#faf9f6]"
                      }`}
                    >
                      <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="visualiser-file-input"
                        onChange={(e) => {
                          take(e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                      {preview ? (
                        <div className="flex flex-col items-center gap-4 sm:flex-row sm:text-left">
                          <img
                            src={preview}
                            alt="Your room"
                            data-testid="visualiser-preview"
                            className="h-32 w-44  object-cover"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold">
                              {file.name}
                            </div>
                            <div className="text-xs text-[color:var(--s-mute)]">
                              We will keep your room exactly as it is and change
                              only the counters.
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                              <button
                                type="button"
                                data-testid="visualiser-run"
                                disabled={!!busy}
                                onClick={() => run()}
                                className="s-btn disabled:opacity-50"
                              >
                                <Sparkle size={14} weight="bold" />
                                {busy === "upload"
                                  ? "Painting your counters…"
                                  : `Visualize with ${stone || "this stone"}`}
                              </button>
                              <button
                                type="button"
                                data-testid="visualiser-clear"
                                onClick={() => setFile(null)}
                                className="s-icon-btn !h-12 !w-12"
                                aria-label="Remove photo"
                              >
                                <X size={16} weight="bold" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <span className="mx-auto flex h-14 w-14 items-center justify-center  bg-white text-[color:var(--s-ink)] shadow-[0_1px_2px_rgba(35,30,20,0.06)]">
                            <UploadSimple size={24} weight="bold" />
                          </span>
                          <div className="mt-4 text-lg font-semibold tracking-tight">
                            Upload a photo of your space
                          </div>
                          <p className="mx-auto mt-1 max-w-md text-sm text-[color:var(--s-mute)]">
                            Drag and drop a photo of your kitchen or bathroom,
                            or click to browse. JPG, PNG or WEBP up to 12 MB.
                          </p>
                          <button
                            type="button"
                            data-testid="visualiser-upload-btn"
                            onClick={() => inputRef.current?.click()}
                            className="s-btn mt-5"
                          >
                            <UploadSimple size={14} weight="bold" /> Upload
                            image
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {busy && busy !== "upload" && (
                    <div
                      className="mt-6 flex aspect-[3/2] w-full animate-pulse items-center justify-center  bg-[#f3f1ec] text-sm text-[color:var(--s-mute)]"
                      data-testid="visualiser-loading"
                    >
                      Painting {stone || "this stone"} into the room…
                    </div>
                  )}

                  {samples.length > 0 && !busy && (
                    <div className="mt-6" data-testid="visualiser-samples">
                      {cfg.photo_upload && (
                        <div className="flex items-center gap-3 text-xs font-medium uppercase tracking-widest text-[color:var(--s-mute)]">
                          <span className="h-px flex-1 bg-[#e6e1d8]" /> or
                          <span className="h-px flex-1 bg-[#e6e1d8]" />
                        </div>
                      )}
                      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="text-base font-semibold tracking-tight">
                            Don&apos;t have a photo? Try a sample room
                          </div>
                          <div className="text-xs text-[color:var(--s-mute)]">
                            Pick a room and we paint {stone || "this stone"}{" "}
                            onto its counters.
                          </div>
                        </div>
                        <div className="flex items-center gap-1  bg-[#f3f1ec] p-1">
                          {GROUPS.map(([key, label]) => (
                            <button
                              key={key}
                              type="button"
                              data-testid={`visualiser-group-${key}`}
                              onClick={() => setGroup(key)}
                              className={` px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                                group === key
                                  ? "bg-[#2e2c29] text-white"
                                  : "text-[color:var(--s-mute)] hover:text-[color:var(--s-ink)]"
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="relative mt-4">
                        <div
                          ref={stripRef}
                          className="no-bar flex gap-3 overflow-x-auto pb-1"
                        >
                          {samples.map((r) => (
                            <button
                              key={r.key}
                              type="button"
                              data-testid={`visualiser-sample-${r.key}`}
                              onClick={() => run(r.key)}
                              className="group relative h-36 w-56 shrink-0 overflow-hidden  bg-[#eeece7] text-left"
                            >
                              <img
                                src={r.url}
                                alt={r.label}
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                              />
                              <span className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent px-3 pb-2.5 pt-8 text-xs font-medium text-white">
                                {r.label}
                                <Sparkle size={13} weight="bold" />
                              </span>
                            </button>
                          ))}
                        </div>
                        {samples.length > 2 && (
                          <>
                            <button
                              type="button"
                              onClick={() => scroll(-1)}
                              aria-label="Previous rooms"
                              className="absolute -left-2 top-1/2 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow lg:flex"
                            >
                              <CaretLeft size={14} weight="bold" />
                            </button>
                            <button
                              type="button"
                              onClick={() => scroll(1)}
                              aria-label="More rooms"
                              className="absolute -right-2 top-1/2 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow lg:flex"
                            >
                              <CaretRight size={14} weight="bold" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {error && !busy && (
                <p
                  className="mt-4 text-sm text-[#a3412f]"
                  data-testid="visualiser-error"
                >
                  {error}
                </p>
              )}
            </div>
          )}

          {cfg.instant_quote && (
            <InstantQuote slabKey={slabKey} stone={stone} onQuote={onQuote} />
          )}
        </div>
      </div>
    </section>
  );
};

// Ballpark price from the shop's own pricing — a slider for size plus the usual add-ons
const InstantQuote = ({ slabKey, stone, onQuote }) => {
  const [sqft, setSqft] = useState(45);
  const [edge, setEdge] = useState("Eased");
  const [sink, setSink] = useState(true);
  const [splash, setSplash] = useState(false);
  const [est, setEst] = useState(null);
  const [off, setOff] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      fetch(`${API}/public/estimate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          key: slabKey,
          sqft,
          edge,
          sink,
          backsplash_sqft: splash ? Math.round(sqft * 0.35) : 0,
        }),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(r)))
        .then(setEst)
        .catch(() => setOff(true));
    }, 250);
    return () => clearTimeout(t);
  }, [slabKey, sqft, edge, sink, splash]);

  if (off) return null;
  const edges = est?.edges?.length ? est.edges : ["Eased"];

  return (
    <div
      className="s-card s-still flex min-w-0 flex-col !p-6 sm:!p-8"
      data-testid="instant-quote"
    >
      <div className="text-sm text-[color:var(--s-mute)]">Instant estimate</div>
      <div
        className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl"
        data-testid="instant-quote-range"
      >
        {est ? `${money(est.low)} – ${money(est.high)}` : "—"}
      </div>
      <p className="mt-1 text-xs text-[color:var(--s-mute)]">
        Installed, with tax, for {stone || "this stone"}. A firm price follows a
        free template.
      </p>

      <label className="mt-6 block">
        <span className="flex items-center justify-between text-sm">
          <span className="font-medium">Counter area</span>
          <span className="font-semibold" data-testid="instant-quote-sqft">
            {sqft} sq ft
          </span>
        </span>
        <input
          type="range"
          min="15"
          max="150"
          step="5"
          value={sqft}
          onChange={(e) => setSqft(Number(e.target.value))}
          data-testid="instant-quote-slider"
          className="mt-2 w-full accent-[#2e2c29]"
        />
        <span className="mt-1 flex justify-between text-[11px] text-[color:var(--s-mute)]">
          <span>Vanity</span>
          <span>Average kitchen ≈ 45</span>
          <span>Large</span>
        </span>
      </label>

      <label className="mt-5 block text-sm">
        <span className="font-medium">Edge profile</span>
        <select
          value={edge}
          onChange={(e) => setEdge(e.target.value)}
          data-testid="instant-quote-edge"
          className="s-round mt-2 h-12 w-full !rounded-xl !border-[#e6e1d8] bg-[#faf9f6] px-4 text-sm"
        >
          {edges.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </select>
      </label>

      <div className="mt-4 grid grid-cols-2 gap-2">
        {[
          ["sink", "Sink cut-out", sink, setSink],
          ["splash", "Backsplash", splash, setSplash],
        ].map(([key, label, on, set]) => (
          <button
            key={key}
            type="button"
            data-testid={`instant-quote-${key}`}
            onClick={() => set(!on)}
            className={`s-chip !flex-row !justify-between !px-4 !py-3 text-sm font-medium transition-colors duration-200 ${
              on ? "!bg-[#2e2c29] !text-white" : ""
            }`}
          >
            {label}
            <span
              className={`h-4 w-4 rounded-full border ${
                on ? "border-white bg-white" : "border-[#c9c3b8]"
              }`}
            />
          </button>
        ))}
      </div>

      {est && (
        <dl
          className="mt-5 space-y-1.5 text-sm"
          data-testid="instant-quote-lines"
        >
          {est.lines.map((ln) => (
            <div key={ln.label} className="flex justify-between gap-4">
              <dt className="text-[color:var(--s-mute)]">{ln.label}</dt>
              <dd className="font-medium">{money(ln.amount)}</dd>
            </div>
          ))}
          <div className="flex justify-between gap-4 border-t border-[#e6e1d8] pt-2">
            <dt className="text-[color:var(--s-mute)]">Tax</dt>
            <dd className="font-medium">{money(est.tax)}</dd>
          </div>
        </dl>
      )}

      <button
        type="button"
        data-testid="instant-quote-cta"
        onClick={onQuote}
        className="s-btn mt-6 w-full justify-center"
      >
        Lock in a real quote
      </button>
    </div>
  );
};

export default SlabVisualiser;
